<script setup>
import { computed } from 'vue'

/**
 * The component's inherited properties.
 */
const props = defineProps({
    /**
     * The given load.
     *
     * @type {Object}
     */
    value: {
        type: Object,
        required: true,
    },
})

/**
 * The carrier name.
 *
 * @return {String}
 */
const carrierName = computed(() => {
    return props.value?.carrier?.name
})

/**
 * The load's drivers.
 *
 * @return {Array}
 */
const drivers = computed(() => {
    return props.value?.drivers || []
})

/**
 * The load's trailers.
 *
 * @return {Array}
 */
const trailers = computed(() => {
    return (
        props.value?.trailers.map((trailer) => trailer.name).join(', ') || '--'
    )
})

/**
 * The load's truck name.
 *
 * @return {String}
 */
const truckName = computed(() => {
    return props.value?.truck?.name || '--'
})
</script>

<template>
    <div>
        <div class="flex">
            <div
                v-if="carrierName"
                class="overflow-x-auto me-10"
                :class="drivers.length > 1 ? 'w-80' : 'w-60'"
            >
                <v-icon
                    icon="far fa-globe"
                    color="primary"
                    size="x-small"
                    class="me-1"
                >
                </v-icon>
                <span data-test="assignment-carrier-name">
                    {{ carrierName }}
                </span>
            </div>
            <div
                v-if="!carrierName"
                class="flex items-center overflow-x-auto me-5 w-52"
                data-test="assignment-carrier-icon"
            >
                <v-icon
                    icon="far fa-user"
                    color="primary"
                    size="x-small"
                    class="me-1"
                >
                </v-icon>
                <span
                    v-for="(driver, index) in drivers"
                    :key="index"
                    class="flex items-center"
                    :data-test="`assignment-driver-${driver?.id}`"
                >
                    {{ driver?.name || '--' }} 
                    <span 
                        v-if="index < drivers.length - 1">, &nbsp; </span
                    >
                    <a
                        v-if="driver?.personal?.mobile_phone"
                        :href="`tel:+1${driver?.personal?.mobile_phone}`"
                        class="flex items-center me-0.5"
                    >
                        <v-icon
                            icon="far fa-circle-phone"
                            size="x-small"
                            color="grey"
                            class="m-1"
                        >
                        </v-icon>
                    </a>
                    <a
                        v-if="driver?.personal?.email"
                        :href="`mailto:${driver?.personal?.email}`"
                        class="flex items-center me-0.5"
                    >
                        <v-icon
                            icon="far fa-circle-envelope"
                            size="x-small"
                            color="grey"
                        >
                        </v-icon>
                    </a>
                </span>
            </div>
            <div
                v-if="!carrierName"
                class="flex items-center w-32 overflow-x-auto me-5"
            >
                <v-icon
                    icon="far fa-truck-flatbed"
                    color="primary"
                    size="x-small"
                    class="me-1"
                >
                </v-icon>
                <span data-test="assignment-truck-name">
                    {{ truckName }}
                </span>
            </div>
            <div class="flex items-center overflow-x-auto w-52">
                <v-icon
                    icon="far fa-trailer"
                    color="primary"
                    size="x-small"
                    class="me-1"
                >
                </v-icon>
                <span data-test="assignment-trailers">
                    {{ trailers }}
                </span>
            </div>
        </div>
    </div>
</template>
